<template>
  <div class="boss-dashboard">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <div class="loading-content">
        <i class="el-icon-loading"></i>
        <span>数据加载中...</span>
      </div>
    </div>

    <!-- 工作台内容区域 -->
    <div class="workbench-content" v-if="!loading && workbenchItems && workbenchItems.length > 0">
      <div class="workbench-header">
        <i class="el-icon-s-platform"></i>
        <span>{{ currentRole.roleName }}工作台</span>
        <el-tag type="primary" size="small">{{ workbenchItems.length }}个组件</el-tag>
      </div>

      <!-- 骨架屏布局 -->
      <div class="layout-container">
        <!-- 第一行：日历组件和统计卡片 -->
        <div class="row first-row">
          <!-- 修复日历组件结构 -->
          <div class="calendar-container" v-if="hasCalendarComponent">
            <div class="section-header">
              <i class="el-icon-date"></i>
              <span>日历组件</span>
              <!-- 添加日期范围选择器 -->
              <div class="date-range-selector">
                <el-date-picker v-model="dateRange" type="daterange" range-separator="至"
                                start-placeholder="开始日期" end-placeholder="结束日期" value-format="yyyy-MM-dd HH:mm:ss"
                                :default-time="['00:00:00', '23:59:59']" size="small"
                                @change="handleDateRangeChange"></el-date-picker>
              </div>
            </div>
            <div class="calendar-content">
              <div class="calendar-wrapper">
                <div class="calendar-header">
                  <el-button icon="el-icon-arrow-left" size="mini" @click="prevMonth"></el-button>
                  <span class="current-month">{{ currentYear }}年{{ currentMonth }}月</span>
                  <el-button icon="el-icon-arrow-right" size="mini" @click="nextMonth"></el-button>
                </div>
                <div class="calendar-weekdays">
                  <div v-for="day in ['日', '一', '二', '三', '四', '五', '六']" :key="day" class="weekday">
                    {{ day }}</div>
                </div>
                <div class="calendar-days">
                  <div v-for="day in calendarDays" :key="day.date" :class="['calendar-day', {
                      'other-month': !day.isCurrentMonth,
                      'today': day.isToday,
                      'selected': day.isSelected,
                      'has-event': day.hasEvent
                    }]">
                    <!--                    @click="selectDate(day)"-->
                    <span class="day-number">{{ day.day }}</span>
                    <div v-if="day.hasEvent" class="event-indicator"></div>
                    <div v-if="day.isToday" class="today-indicator">今</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 统计卡片组件 -->
          <div class="col cards-col" v-if="hasCardComponent">
            <div class="cards-container">
              <div class="section-header">
                <i class="el-icon-data-board"></i>
                <span>数据统计</span>
				<!-- 添加日期范围选择器 -->
				<div class="date-range-selector">
					<el-date-picker v-model="dateRange" type="daterange" range-separator="至"
						start-placeholder="开始日期" end-placeholder="结束日期" value-format="yyyy-MM-dd HH:mm:ss"
						:default-time="['00:00:00', '23:59:59']" size="small"
						@change="handleDateRangeChange"></el-date-picker>
				</div>
              </div>
              <div class="cards-grid">
                <el-card v-for="(card, index) in cardData" :key="index" class="stat-card" shadow="hover"
                         @click.native="handleCardClick(card)">
                  <div class="card-content-wrapper">
                    <div class="card-icon" :style="{ background: card.color }">
                      <i :class="card.icon"></i>
                    </div>
                    <div class="card-info">
                      <div class="card-value">{{ card.value }}</div>
                      <div class="card-label">{{ card.label }}</div>
                      <div v-if="card.trend" class="card-trend" :class="card.trend.type">
                        <i :class="card.trend.icon"></i>
                        <span>{{ card.trend.value }}</span>
                      </div>
                    </div>
                  </div>
                </el-card>
              </div>
            </div>
          </div>
        </div>

        <!-- 第二行：统计进度和环形百分比 -->
        <div class="row second-row" v-if="hasProgressOrRingComponents">
          <!-- 统计进度组件 -->
          <div class="col progress-col" v-if="hasProgressComponent">
            <div class="progress-container">
              <div class="section-header">
                <i class="el-icon-trend-charts"></i>
                <span>收入进度</span>
              </div>
              <div class="progress-content">
                <div class="progress-chart">
                  <div v-for="item in progressData" :key="item.name" class="progress-item">
                    <div class="progress-info">
                      <span class="progress-label">{{ item.name }}</span>
                      <span class="progress-value">{{ item.value }}%</span>
                    </div>
                    <el-progress :percentage="item.value" :color="item.color" :show-text="false"
                                 :stroke-width="8"></el-progress>
                    <div class="progress-amount">¥{{ formatNumberWithCommas(item.amount) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 环形百分比组件 -->
          <div class="col ring-col" v-if="hasRingComponent">
            <div class="ring-container">
              <div class="section-header">
                <i class="el-icon-c-scale-to-original"></i>
                <span>费用占比</span>
              </div>
              <div class="ring-content">
                <div class="ring-charts">
                  <div v-for="item in ringData" :key="item.name" class="ring-item">
                    <div class="ring-chart">
                      <div class="ring-progress" :style="{
                        background: `conic-gradient(${item.color} 0% ${item.value}%, #eee ${item.value}% 100%)` 
                      }">
                        <div class="ring-inner">
                          <span class="ring-value">{{ item.value }}%</span>
                        </div>
                      </div>
                    </div>
                    <div class="ring-info">
                      <span class="ring-label">{{ item.name }}</span>
                      <span class="ring-amount">¥{{ formatNumberWithCommas(item.amount) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 第三行：图表组件 -->
        <div class="row third-row" v-if="hasChartComponent">
          <div class="col chart-col">
            <div class="chart-container">
              <div class="section-header">
                <i class="el-icon-pie-chart"></i>
                <span>收入趋势</span>
                <el-select v-model="chartType" size="small" class="chart-type-selector"
                           @change="updateChart">
                  <el-option label="折线图" value="line"></el-option>
                  <el-option label="柱状图" value="bar"></el-option>
                  <el-option label="饼图" value="pie"></el-option>
                </el-select>
              </div>
              <div class="chart-content">
                <div id="chart-container" style="width: 100%; height: 100%;"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 第四行：表格组件 -->
        <div class="row fourth-row" v-if="hasTableComponent">
          <div class="col table-col">
            <div class="table-container">
              <div class="section-header">
                <i class="el-icon-s-grid"></i>
                <span>详细数据</span>
              </div>
              <div class="table-content">
                <el-table :data="tableData" height="400" style="width: 100%" stripe border
                          v-loading="tableLoading">
                  <el-table-column v-for="column in tableColumns" :key="column.prop"
                                   :prop="column.prop" :label="column.label" :width="column.width"
                                   :formatter="column.formatter"></el-table-column>
                  <el-table-column label="操作" width="100" fixed="right">
                    <template slot-scope="scope">
                      <el-button type="text" size="mini"
                                 @click="viewDetails(scope.row)">详情</el-button>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-else-if="!loading" class="empty-workbench">
      <div class="empty-content">
        <i class="el-icon-s-platform"></i>
        <h3>暂无工作台内容</h3>
        <p>当前角色工作台尚未配置任何组件</p>
        <el-button type="primary" @click="$emit('show-edit-layout')" class="setup-layout-btn">
          <i class="el-icon-edit"></i>
          立即设置工作台
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  statist
} from "@/api/index.js";
import * as echarts from 'echarts';

export default {
  name: 'BossDashboard',
  props: {
    userInfo: Object,
    clinicInfo: Object,
    currentRole: Object,
    workbenchItems: {
      type: Array,
      default: () => []
    }
  },
  data() {
    const today = new Date();
    const startTime = new Date(today);
    startTime.setHours(0, 0, 0, 0);
    const endTime = new Date(today);
    endTime.setHours(23, 59, 59, 999);

    return {
      loading: false,
      tableLoading: false,
      chartType: 'line',
      chart: null,
      currentYear: today.getFullYear(),
      currentMonth: today.getMonth() + 1,
      selectedDate: today.toISOString().split('T')[0], // 默认选中今天

      // 日期范围
      dateRange: [
        this.formatDateTime(startTime),
        this.formatDateTime(endTime)
      ],

      // API数据
      apiData: {},

      // 日历数据
      calendarDays: [],
      todayEvents: [],

      // 统计进度数据
      progressData: [],

      // 环形百分比数据
      ringData: [],

      // 统计卡片数据（8个）
      cardData: [],

      // 表格数据
      tableData: [],
      // 更新表格列配置
      // 更新表格列配置，确保固定列在右侧
      tableColumns: [{
        prop: 'date',
        label: '日期',
        width: 100,
        fixed: 'left',
        formatter: (row) => this.formatDate(row.date)
      },
        {
          prop: 'regMoney',
          label: '挂号金额',
          width: 120,
          formatter: (row) => this.formatCurrency(row.regMoney)
        },
        {
          prop: 'regNum',
          label: '挂号人次',
          width: 120
        },
        {
          prop: 'drugRxMoney',
          label: '西药处方金额',
          width: 140,
          formatter: (row) => this.formatCurrency(row.drugRxMoney)
        },
        {
          prop: 'drugRxNum',
          label: '西药处方数',
          width: 120
        },
        {
          prop: 'tcmRxMoney',
          label: '中药处方金额',
          width: 140,
          formatter: (row) => this.formatCurrency(row.tcmRxMoney)
        },
        {
          prop: 'tcmRxNum',
          label: '中药处方数',
          width: 120
        },
        {
          prop: 'visitChargeMoney',
          label: '就诊收费金额',
          width: 140,
          formatter: (row) => this.formatCurrency(row.visitChargeMoney)
        },
        {
          prop: 'visitNum',
          label: '就诊人次',
          width: 120
        },
        // 新增字段按照接口返回格式
        {
          prop: 'drugCkMoney',
          label: '西药出库金额',
          width: 140,
          formatter: (row) => this.formatCurrency(row.drugCkMoney)
        },
        {
          prop: 'drugCkNum',
          label: '西药出库数',
          width: 120
        },
        {
          prop: 'drugRkMoney',
          label: '西药入库金额',
          width: 140,
          formatter: (row) => this.formatCurrency(row.drugRkMoney)
        },
        {
          prop: 'drugRkNum',
          label: '西药入库数',
          width: 120
        },
        {
          prop: 'rxMoney',
          label: '处方总金额',
          width: 120,
          formatter: (row) => this.formatCurrency(row.rxMoney)
        },
        {
          prop: 'rxNum',
          label: '处方总数',
          width: 90
        },
        {
          prop: 'tcmCkMoney',
          label: '中药出库金额',
          width: 120,
          formatter: (row) => this.formatCurrency(row.tcmCkMoney)
        },
        {
          prop: 'tcmCkNum',
          label: '中药出库数',
          width: 120
        },
        {
          prop: 'tcmRkMoney',
          label: '中药入库金额',
          width: 140,
          formatter: (row) => this.formatCurrency(row.tcmRkMoney)
        },
        {
          prop: 'tcmRkNum',
          label: '中药入库数',
          width: 120
        },
        {
          prop: 'visitChargeNum',
          label: '就诊收费人次',
          width: 160
        },
      ],

      // 图表数据
      chartData: {}
    };
  },
  computed: {
    hasLayoutSuggestions() {
      return this.workbenchItems && this.workbenchItems.length > 0 && this.workbenchItems.length < 3;
    },

    calendarItems() {
      return this.workbenchItems.filter(item => item.componentType === 'calendar');
    },

    progressItems() {
      return this.workbenchItems.filter(item => item.componentType === 'progress');
    },

    ringItems() {
      return this.workbenchItems.filter(item => item.componentType === 'ring-percent');
    },

    chartItems() {
      return this.workbenchItems.filter(item => item.componentType === 'chart');
    },

    tableItems() {
      return this.workbenchItems.filter(item => item.componentType === 'stats-table');
    },

    cardItems() {
      return this.workbenchItems.filter(item => item.componentType === 'stats-card');
    },

    hasCalendarComponent() {
      return this.calendarItems.length > 0;
    },

    hasProgressComponent() {
      return this.progressItems.length > 0;
    },

    hasRingComponent() {
      return this.ringItems.length > 0;
    },

    hasProgressOrRingComponents() {
      return this.hasProgressComponent || this.hasRingComponent;
    },

    hasChartComponent() {
      return this.chartItems.length > 0;
    },

    hasTableComponent() {
      return this.tableItems.length > 0;
    },

    hasCardComponent() {
      return this.cardItems.length > 0;
    }
  },
  watch: {
    workbenchItems: {
      handler(newItems) {
        // console.log('BossDashboard工作台项目更新:', newItems);
        this.debugComponentDisplay();
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    // console.log('BossDashboard组件挂载，工作台项目:', this.workbenchItems);
    this.debugComponentDisplay();
    this.initCalendar();

    // 延迟加载统计数据，确保DOM渲染完成
    this.$nextTick(() => {
      setTimeout(() => {
        this.loadStatistData();
        this.initChart();
      }, 500);
    });
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    // 格式化日期时间
    formatDateTime(date) {
      const pad = num => num.toString().padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getMonth() + 1}月${date.getDate()}日`;
    },

    // 格式化货币
    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '0.00';
      return parseFloat(amount).toFixed(2);
    },

    debugComponentDisplay() {
      // console.log('=== BossDashboard组件显示状态 ===');
      // console.log('工作台项目:', this.workbenchItems);
      // console.log('日历组件:', this.hasCalendarComponent, this.calendarItems);
      // console.log('统计进度:', this.hasProgressComponent, this.progressItems);
      // console.log('环形百分比:', this.hasRingComponent, this.ringItems);
      // console.log('图表组件:', this.hasChartComponent, this.chartItems);
      // console.log('统计表格:', this.hasTableComponent, this.tableItems);
      // console.log('统计卡片:', this.hasCardComponent, this.cardItems);
      // console.log('==============================');
    },

    // 初始化日历
    initCalendar() {
      this.generateCalendarDays();
      this.loadTodayEvents();
    },

    // 生成日历天数
    generateCalendarDays() {
      const days = [];
      const date = new Date(this.currentYear, this.currentMonth - 1, 1);
      const today = new Date();
      const selectedDate = new Date(this.selectedDate);

      // 上个月的最后几天
      const firstDay = date.getDay();
      const lastMonthLastDay = new Date(this.currentYear, this.currentMonth - 1, 0).getDate();

      for (let i = firstDay - 1; i >= 0; i--) {
        const dayNum = lastMonthLastDay - i;
        const dayDate = new Date(this.currentYear, this.currentMonth - 2, dayNum);
        days.push({
          day: dayNum,
          date: dayDate.toISOString().split('T')[0],
          isCurrentMonth: false,
          isToday: this.isSameDay(dayDate, today),
          isSelected: this.isSameDay(dayDate, selectedDate),
          hasEvent: Math.random() > 0.7
        });
      }

      // 当前月的天数
      const currentMonthDays = new Date(this.currentYear, this.currentMonth, 0).getDate();

      for (let i = 1; i <= currentMonthDays; i++) {
        const dayDate = new Date(this.currentYear, this.currentMonth - 1, i);
        days.push({
          day: i,
          date: dayDate.toISOString().split('T')[0],
          isCurrentMonth: true,
          isToday: this.isSameDay(dayDate, today),
          isSelected: this.isSameDay(dayDate, selectedDate),
          hasEvent: Math.random() > 0.7
        });
      }

      // 下个月的前几天
      const lastDay = new Date(this.currentYear, this.currentMonth - 1, currentMonthDays).getDay();
      const nextDays = 6 - lastDay;

      for (let i = 1; i <= nextDays; i++) {
        const dayDate = new Date(this.currentYear, this.currentMonth, i);
        days.push({
          day: i,
          date: dayDate.toISOString().split('T')[0],
          isCurrentMonth: false,
          isToday: this.isSameDay(dayDate, today),
          isSelected: this.isSameDay(dayDate, selectedDate),
          hasEvent: Math.random() > 0.7
        });
      }

      this.calendarDays = days;
    },

    // 判断是否为同一天
    isSameDay(date1, date2) {
      return date1.getFullYear() === date2.getFullYear() &&
          date1.getMonth() === date2.getMonth() &&
          date1.getDate() === date2.getDate();
    },

    // 加载今日事件
    loadTodayEvents() {
      this.todayEvents = [{
        id: 1,
        time: '09:00',
        title: '团队晨会'
      },
        {
          id: 2,
          time: '14:00',
          title: '客户洽谈'
        },
        {
          id: 3,
          time: '16:30',
          title: '项目评审'
        }
      ];
    },

    // 上个月
    prevMonth() {
      if (this.currentMonth === 1) {
        this.currentYear--;
        this.currentMonth = 12;
      } else {
        this.currentMonth--;
      }
      this.generateCalendarDays();
    },

    // 下个月
    nextMonth() {
      if (this.currentMonth === 12) {
        this.currentYear++;
        this.currentMonth = 1;
      } else {
        this.currentMonth++;
      }
      this.generateCalendarDays();
    },

    // 选择日期
    selectDate(day) {
      this.selectedDate = day.date;
      const selectedDate = new Date(day.date);
      const startTime = new Date(selectedDate);
      startTime.setHours(0, 0, 0, 0);
      const endTime = new Date(selectedDate);
      endTime.setHours(23, 59, 59, 999);

      this.dateRange = [
        this.formatDateTime(startTime),
        this.formatDateTime(endTime)
      ];

      this.generateCalendarDays();
      this.loadStatistData();
    },

    // 处理日期范围变化
    handleDateRangeChange(val) {
      if (val && val.length === 2) {
        this.loadStatistData();
      } else {
        // 重置为今天
        const today = new Date();
        const startTime = new Date(today);
        startTime.setHours(0, 0, 0, 0);
        const endTime = new Date(today);
        endTime.setHours(23, 59, 59, 999);

        this.dateRange = [
          this.formatDateTime(startTime),
          this.formatDateTime(endTime)
        ];
      }
    },

    // 加载统计数据
    async loadStatistData() {
      this.loading = true;
      this.tableLoading = true;

      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};
        const clinicId = userInfo.clinicId || 0;

        const [startTime, endTime] = this.dateRange;

        const res = await statist({
          clinicId: clinicId,
          startTime: startTime,
          endTime: endTime
        });

        if (res && res.code === 200) {
          this.apiData = res.data || res;
          this.processApiData();
          this.initChart();
        } else {
          this.useMockData();
        }
      } catch (error) {
        console.error('加载统计数据失败:', error);
        this.useMockData();
      } finally {
        this.loading = false;
        this.tableLoading = false;
      }
    },

    // 处理API数据
    processApiData() {
      // 更新统计卡片数据
      this.updateCardData();

      // 更新进度数据
      this.updateProgressData();

      // 更新环形百分比数据
      this.updateRingData();

      // 更新表格数据
      this.updateTableData();

      // 更新图表数据
      this.updateChartData();
    },

    // 更新统计卡片数据（8个）
    updateCardData() {
      if (!this.apiData) return;

      const {
        drugRxMoney = 0,
        drugRxNum = 0,
        regMoney = 0,
        regNum = 0,
        rxMoney = 0,
        rxNum = 0,
        tcmRxMoney = 0,
        tcmRxNum = 0,
        visitChargeMoney = 0,
        visitChargeNum = 0,
        visitNum = 0
      } = this.apiData;

      this.cardData = [{
        label: '挂号金额',
        value: `¥${this.formatNumberWithCommas(regMoney)}`,
        icon: 'el-icon-money',
        color: '#409EFF',
        trend: {
          type: 'up',
          icon: 'el-icon-top',
          value: '+12%'
        }
      },
        {
          label: '挂号人次',
          value: this.formatNumberWithCommas(regNum),
          icon: 'el-icon-user',
          color: '#67C23A',
          trend: {
            type: 'up',
            icon: 'el-icon-top',
            value: '+8%'
          }
        },
        {
          label: '西药处方金额',
          value: `¥${this.formatNumberWithCommas(drugRxMoney)}`,
          icon: 'el-icon-chargingPrice',
          color: '#E6A23C',
          trend: {
            type: 'down',
            icon: 'el-icon-bottom',
            value: '-5%'
          }
        },
        {
          label: '西药处方数量',
          value: this.formatNumberWithCommas(drugRxNum),
          icon: 'el-icon-tickets',
          color: '#F56C6C',
          trend: {
            type: 'up',
            icon: 'el-icon-top',
            value: '+3%'
          }
        },
        {
          label: '中药处方金额',
          value: `¥${this.formatNumberWithCommas(tcmRxMoney)}`,
          icon: 'el-icon-mzy',
          color: '#909399',
          trend: {
            type: 'up',
            icon: 'el-icon-top',
            value: '+15%'
          }
        },
        {
          label: '中药处方数量',
          value: this.formatNumberWithCommas(tcmRxNum),
          icon: 'el-icon-ddzy',
          color: '#B37FEB',
          trend: {
            type: 'down',
            icon: 'el-icon-bottom',
            value: '-2%'
          }
        },
        {
          label: '就诊收费金额',
          value: `¥${this.formatNumberWithCommas(visitChargeMoney)}`,
          icon: 'el-icon-zhifubao',
          color: '#8E44AD',
          trend: {
            type: 'up',
            icon: 'el-icon-top',
            value: '+10%'
          }
        },
        {
          label: '就诊人次',
          value: this.formatNumberWithCommas(visitNum),
          icon: 'el-icon-dangan',
          color: '#FF6B6B',
          trend: {
            type: 'up',
            icon: 'el-icon-top',
            value: '+7%'
          }
        }
      ];
    },

    // 更新进度数据
    updateProgressData() {
      if (!this.apiData) return;

      const {
        regMoney = 0,
        rxMoney = 0,
        visitChargeMoney = 0
      } = this.apiData;

      const total = regMoney + rxMoney + visitChargeMoney;
      const maxValue = Math.max(regMoney, rxMoney, visitChargeMoney, 1);

      this.progressData = [{
        name: '挂号金额',
        value: total > 0 ? Math.round((regMoney / maxValue) * 100) : 0,
        amount: regMoney,
        color: '#409EFF'
      },
        {
          name: '处方金额',
          value: total > 0 ? Math.round((rxMoney / maxValue) * 100) : 0,
          amount: rxMoney,
          color: '#67C23A'
        },
        {
          name: '就诊收费金额',
          value: total > 0 ? Math.round((visitChargeMoney / maxValue) * 100) : 0,
          amount: visitChargeMoney,
          color: '#E6A23C'
        }
      ];
    },

    // 更新环形百分比数据
    updateRingData() {
      if (!this.apiData) return;

      const {
        regMoney = 0,
        drugRxMoney = 0,
        tcmRxMoney = 0,
        visitChargeMoney = 0
      } = this.apiData;

      const total = regMoney + drugRxMoney + tcmRxMoney + visitChargeMoney;

      if (total === 0) {
        this.ringData = [{
          name: '挂号费',
          value: 25,
          amount: 0,
          color: '#409EFF'
        },
          {
            name: '西药费',
            value: 35,
            amount: 0,
            color: '#67C23A'
          },
          {
            name: '中药费',
            value: 20,
            amount: 0,
            color: '#E6A23C'
          },
          {
            name: '诊疗费',
            value: 20,
            amount: 0,
            color: '#F56C6C'
          }
        ];
        return;
      }

      this.ringData = [{
        name: '挂号费',
        value: Math.round((regMoney / total) * 100),
        amount: regMoney,
        color: '#409EFF'
      },
        {
          name: '西药费',
          value: Math.round((drugRxMoney / total) * 100),
          amount: drugRxMoney,
          color: '#67C23A'
        },
        {
          name: '中药费',
          value: Math.round((tcmRxMoney / total) * 100),
          amount: tcmRxMoney,
          color: '#E6A23C'
        },
        {
          name: '诊疗费',
          value: Math.round((visitChargeMoney / total) * 100),
          amount: visitChargeMoney,
          color: '#F56C6C'
        }
      ];
    },

    // 更新表格数据方法
    updateTableData() {
      if (!this.apiData) return;

      // 使用接口返回的实际数据
      this.tableData = [{
        date: new Date().toISOString().split('T')[0],
        // 直接使用接口返回的数据
        drugCkMoney: this.apiData.drugCkMoney || 0,
        drugCkNum: this.apiData.drugCkNum || 0,
        drugRkMoney: this.apiData.drugRkMoney || 0,
        drugRkNum: this.apiData.drugRkNum || 0,
        drugRxMoney: this.apiData.drugRxMoney || 0,
        drugRxNum: this.apiData.drugRxNum || 0,
        regMoney: this.apiData.regMoney || 0,
        regNum: this.apiData.regNum || 0,
        rxMoney: this.apiData.rxMoney || 0,
        rxNum: this.apiData.rxNum || 0,
        tcmCkMoney: this.apiData.tcmCkMoney || 0,
        tcmCkNum: this.apiData.tcmCkNum || 0,
        tcmRkMoney: this.apiData.tcmRkMoney || 0,
        tcmRkNum: this.apiData.tcmRkNum || 0,
        tcmRxMoney: this.apiData.tcmRxMoney || 0,
        tcmRxNum: this.apiData.tcmRxNum || 0,
        visitChargeMoney: this.apiData.visitChargeMoney || 0,
        visitChargeNum: this.apiData.visitChargeNum || 0,
        visitNum: this.apiData.visitNum || 0
      }];
    },

    // 更新图表数据
    // 更新图表数据方法
    updateChartData() {
      if (!this.apiData) return;

      // 使用实际数据而不是模拟数据
      this.chartData = {
        dates: this.generateDefaultDates(7),
        regMoney: Array(7).fill(this.apiData.regMoney || 0),
        drugRxMoney: Array(7).fill(this.apiData.drugRxMoney || 0),
        tcmRxMoney: Array(7).fill(this.apiData.tcmRxMoney || 0),
        visitChargeMoney: Array(7).fill(this.apiData.visitChargeMoney || 0)
      };
    },

    // 生成默认日期数组
    generateDefaultDates(days) {
      const dates = [];
      const today = new Date();

      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(today.getDate() - i);
        const month = date.getMonth() + 1;
        const day = date.getDate();
        dates.push(`${month}月${day}日`);
      }

      return dates;
    },

    // 格式化数字
    formatNumberWithCommas(num) {
      if (num === null || num === undefined) return '0';
      const number = typeof num === 'string' ? parseFloat(num) : num;
      if (isNaN(number)) return '0';

      if (Number.isInteger(number)) {
        return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      } else {
        return number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
    },

    // 使用模拟数据
    useMockData() {
      // console.log('使用模拟数据');
      this.apiData = {
        drugRxMoney: 12800,
        drugRxNum: 120,
        regMoney: 5600,
        regNum: 98,
        rxMoney: 12800,
        rxNum: 42,
        tcmRxMoney: 5600,
        tcmRxNum: 37,
        visitChargeMoney: 5600,
        visitChargeNum: 98,
        visitNum: 120
      };

      this.processApiData();
      this.initChart();
    },

    // 初始化图表
    // 修复图表初始化方法
    initChart() {
      if (!this.hasChartComponent) return;

      this.$nextTick(() => {
        setTimeout(() => {
          const chartDom = document.getElementById('chart-container');
          if (!chartDom) {
            console.error('图表容器未找到，延迟重试...');
            setTimeout(() => this.initChart(), 100);
            return;
          }

          try {
            // 确保容器有尺寸
            if (chartDom.offsetWidth === 0 || chartDom.offsetHeight === 0) {
              chartDom.style.width = '100%';
              chartDom.style.height = '300px';
            }

            // 先销毁已有的图表实例
            if (this.chart) {
              this.chart.dispose();
              this.chart = null;
            }

            this.chart = echarts.init(chartDom);
            this.updateChart();

            // 添加窗口大小变化监听
            const resizeHandler = () => {
              if (this.chart) {
                this.chart.resize();
              }
            };

            window.addEventListener('resize', resizeHandler);
            this.$once('hook:beforeDestroy', () => {
              window.removeEventListener('resize', resizeHandler);
            });
          } catch (error) {
            console.error('图表初始化失败:', error);
          }
        }, 300);
      });
    },

    // 更新图表
    updateChart() {
      if (!this.chart) return;

      const option = this.getChartOption();
      this.chart.setOption(option, true);

      // 响应式调整
      window.addEventListener('resize', () => {
        this.chart.resize();
      });
    },

    // 获取图表配置
    getChartOption() {
      const baseOption = {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['挂号金额', '西药处方金额', '中药处方金额', '就诊收费金额']
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        }
      };

      switch (this.chartType) {
        case 'line':
          return {
            ...baseOption,
            xAxis: {
              type: 'category',
              data: this.chartData.dates || ['1月', '2月', '3月', '4月', '5月', '6月']
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '¥{value}'
              }
            },
            series: [{
              name: '挂号金额',
              type: 'line',
              data: this.chartData.regMoney || [2000, 2500, 1800, 3000, 2800, 3200],
              smooth: true,
              itemStyle: {
                color: '#409EFF'
              }
            },
              {
                name: '西药处方金额',
                type: 'line',
                data: this.chartData.drugRxMoney || [3000, 3500, 2800, 4000, 3800, 4200],
                smooth: true,
                itemStyle: {
                  color: '#67C23A'
                }
              },
              {
                name: '中药处方金额',
                type: 'line',
                data: this.chartData.tcmRxMoney || [1000, 1200, 800, 1500, 1300, 1600],
                smooth: true,
                itemStyle: {
                  color: '#E6A23C'
                }
              },
              {
                name: '就诊收费金额',
                type: 'line',
                data: this.chartData.visitChargeMoney || [2500, 3000, 2200, 3500, 3200, 3800],
                smooth: true,
                itemStyle: {
                  color: '#F56C6C'
                }
              }
            ]
          };

        case 'bar':
          return {
            ...baseOption,
            xAxis: {
              type: 'category',
              data: this.chartData.dates || ['1月', '2月', '3月', '4月', '5月', '6月']
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '¥{value}'
              }
            },
            series: [{
              name: '挂号金额',
              type: 'bar',
              data: this.chartData.regMoney || [2000, 2500, 1800, 3000, 2800, 3200],
              itemStyle: {
                color: '#409EFF'
              }
            },
              {
                name: '西药处方金额',
                type: 'bar',
                data: this.chartData.drugRxMoney || [3000, 3500, 2800, 4000, 3800, 4200],
                itemStyle: {
                  color: '#67C23A'
                }
              },
              {
                name: '中药处方金额',
                type: 'bar',
                data: this.chartData.tcmRxMoney || [1000, 1200, 800, 1500, 1300, 1600],
                itemStyle: {
                  color: '#E6A23C'
                }
              },
              {
                name: '就诊收费金额',
                type: 'bar',
                data: this.chartData.visitChargeMoney || [2500, 3000, 2200, 3500, 3200, 3800],
                itemStyle: {
                  color: '#F56C6C'
                }
              }
            ]
          };

        case 'pie':
          const totalRegMoney = this.chartData.regMoney ? this.chartData.regMoney.reduce((a, b) => a + b,
              0) : 15300;
          const totalDrugRxMoney = this.chartData.drugRxMoney ? this.chartData.drugRxMoney.reduce((a, b) =>
              a + b, 0) : 21300;
          const totalTcmRxMoney = this.chartData.tcmRxMoney ? this.chartData.tcmRxMoney.reduce((a, b) => a +
              b, 0) : 7400;
          const totalVisitChargeMoney = this.chartData.visitChargeMoney ? this.chartData.visitChargeMoney
              .reduce((a, b) => a + b, 0) : 18200;

          return {
            tooltip: {
              trigger: 'item',
              formatter: '{b}: ¥{c} ({d}%)'
            },
            legend: {
              orient: 'vertical',
              right: 10,
              top: 'center'
            },
            series: [{
              name: '收入占比',
              type: 'pie',
              radius: ['40%', '70%'],
              avoidLabelOverlap: false,
              itemStyle: {
                borderRadius: 10,
                borderColor: '#fff',
                borderWidth: 2
              },
              label: {
                show: false,
                position: 'center'
              },
              emphasis: {
                label: {
                  show: true,
                  fontSize: '18',
                  fontWeight: 'bold'
                }
              },
              labelLine: {
                show: false
              },
              data: [{
                value: totalRegMoney,
                name: '挂号金额',
                itemStyle: {
                  color: '#409EFF'
                }
              },
                {
                  value: totalDrugRxMoney,
                  name: '西药处方金额',
                  itemStyle: {
                    color: '#67C23A'
                  }
                },
                {
                  value: totalTcmRxMoney,
                  name: '中药处方金额',
                  itemStyle: {
                    color: '#E6A23C'
                  }
                },
                {
                  value: totalVisitChargeMoney,
                  name: '就诊收费金额',
                  itemStyle: {
                    color: '#F56C6C'
                  }
                }
              ]
            }]
          };

        default:
          return {
            ...baseOption,
            xAxis: {
              type: 'category',
              data: this.chartData.dates || ['1月', '2月', '3月', '4月', '5月', '6月']
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '¥{value}'
              }
            },
            series: [{
              name: '挂号金额',
              type: 'line',
              data: this.chartData.regMoney || [2000, 2500, 1800, 3000, 2800, 3200],
              smooth: true,
              itemStyle: {
                color: '#409EFF'
              }
            }]
          };
      }
    },

    // 查看详情
    viewDetails(row) {
      this.$message.success(`查看 ${row.date} 的详细信息`);
      this.$emit('item-click', {
        type: 'table-detail',
        data: row
      });
    },

    handleComponentClick(item) {
      // console.log('点击组件:', item);
      this.$emit('item-click', item);
    },

    getComponentDisplayName(item) {
      return item.name || item.title || item.label || '未命名组件';
    }
  },
}
</script>

<style lang="less" scoped>
.boss-dashboard {
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f7fa;
}

.loading-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;

    i {
      font-size: 32px;
      color: #409EFF;
      margin-bottom: 10px;
      animation: rotating 2s linear infinite;
    }

    span {
      font-size: 16px;
      color: #606266;
    }
  }
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.workbench-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  font-size: 18px;
  font-weight: 600;
  color: #303133;

  i {
    margin-right: 8px;
    color: #409EFF;
    font-size: 20px;
  }
}

// 骨架屏布局容器
.layout-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

// 行布局
.row {
  display: flex;
  gap: 20px;
  min-height: 0;

  /* 第一行：日历和统计卡片 */
  &.first-row {
    height: 480px;

    .calendar-col {
      flex: 2;
      /* 日历组件宽度比例调整为2 */
    }

    .cards-col {
      flex: 3;
      /* 数据统计组件宽度比例调整为3 */
    }
  }

  // 第二行：统计进度和环形百分比
  &.second-row {
    height: 280px;

    .progress-col,
    .ring-col {
      flex: 1;
    }
  }

  // 第三行：图表组件
  &.third-row {
    height: 400px;

    .chart-col {
      flex: 1;
    }
  }

  // 第四行：表格组件
  &.fourth-row {
    height: 488px;

    .table-col {
      flex: 1;
    }
  }
}

// 列布局
.col {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

// 通用容器样式
.calendar-container,
.progress-container,
.ring-container,
.chart-container,
.table-container,
.cards-container {
  background: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #ebeef5;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  }

  .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 1px solid #ebeef5;
    flex-shrink: 0;

    i {
      margin-right: 8px;
      color: #409EFF;
      font-size: 16px;
    }

    span {
      font-weight: 600;
      font-size: 14px;
      color: #3b3b3b;
      margin-right: auto;
    }

    .chart-type-selector {
      width: 140px;
    }

    .date-range-selector {
      margin-left: 12px;

      /deep/ .el-range-editor {
        width: 280px;
      }
    }
  }
}

/* 日历组件样式优化 */
.calendar-container {
  .calendar-content {
    .calendar-wrapper {
      height: auto;
      text-align: center;
      min-height: 200px;

      .calendar-header {
        display: flex;
        justify-content: space-between;

        .current-month {
          color: #303133;
        }
      }

      .calendar-weekdays {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        text-align: center;
        margin-bottom: 8px;

        .weekday {
          font-size: 12px;
          color: #606266;
          /* 黑灰色 */
          font-weight: 500;
          padding: 8px 0;
        }
      }

      .calendar-days {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
        min-height: 150px;

        .calendar-day {
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          cursor: pointer;
          position: relative;
          font-size: 12px;
          transition: all 0.2s ease;
          border: 1px solid transparent;
          color: #606266;
          /* 黑灰色 */
          min-height: 30px;

          .day-number {
            font-size: 12px;
            font-weight: 500;
            z-index: 2;
            color: #606266;
            /* 黑灰色 */
          }

          &.today {
            background: rgba(64, 158, 255, 0.1);
            border-color: #409EFF;
            color: #409EFF;
            font-weight: 600;

            .day-number {
              color: #409EFF;
            }
          }

          &.selected {
            background: #409EFF;
            color: white;

            .day-number {
              color: white;
            }
          }
        }
      }
    }
  }
}

/* 表格容器样式优化 */
.table-container {
  .table-content {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    width: 100%;

    /deep/ .el-table {
      width: 100%;
      min-width: 1200px;
      /* 设置最小宽度确保内容完整显示 */

      .el-table__header-wrapper {
        // overflow-x: auto;
        // overflow-y: hidden;
      }

      .el-table__body-wrapper {
        overflow-x: auto;
        overflow-y: auto;
      }

      /* 确保固定列正确显示 */
      .el-table__fixed,
      .el-table__fixed-right {
        height: 100% !important;
        box-shadow: none;
      }

      .el-table__fixed-right {
        right: 0 !important;
        border-left: 1px solid #ebeef5;
      }

      .el-table__fixed-body-wrapper {
        overflow-x: hidden;
      }

      .el-table__cell {
        white-space: nowrap;
        min-width: 120px;
        color: #606266;
        padding: 8px 0;

        &.is-fixed-left,
        &.is-fixed-right {
          z-index: 3;
          background-color: #fff;
        }
      }

      /* 滚动条样式优化 */
      .el-table__body-wrapper {
        &::-webkit-scrollbar {
          height: 6px;
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;

          &:hover {
            background: #a8a8a8;
          }
        }
      }
    }
  }
}

/* 确保表格父容器宽度限制 */
.row.fourth-row {
  height: 488px;
  overflow: hidden;

  .table-col {
    flex: 1;
    min-width: 0;
    /* 重要：防止flex item溢出 */
    overflow: hidden;
  }
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .table-container .table-content /deep/ .el-table {
    min-width: 1400px;
    /* 在小屏幕上保持足够的宽度 */
  }
}

@media (max-width: 768px) {
  .row.fourth-row {
    height: 400px;
  }

  .table-container .table-content /deep/ .el-table {
    min-width: 1600px;
    /* 在移动端需要更宽的滚动区域 */

    .el-table__cell {
      min-width: 70px;
      font-size: 12px;
    }
  }
}

/* 第一行布局调整 */
.row.first-row {
  height: 480px;
  /* 适当降低高度 */

  .calendar-col {
    flex: 2;
    min-height: 0;
  }

  .cards-col {
    flex: 3;
    min-height: 0;
  }
}

/* 日期选择器样式优化 */
.date-range-selector {
  margin-left: auto;

  /deep/ .el-range-editor {
    width: 280px;

    .el-range-input {
      font-size: 12px;
    }

    .el-range-separator {
      font-size: 12px;
    }
  }
}

// 统计卡片容器
.cards-container {
  .cards-grid {
    flex: 1;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    /* 每行8个卡片 */
    grid-auto-rows: 68px;
    /* 固定高度68px */
    gap: 8px;
    height: 100%;
    overflow-y: auto;
    /* 超出时滚动 */

    .stat-card {
      cursor: pointer;
      transition: all 0.3s ease;
      border: none;
      border-radius: 8px;
      overflow: hidden;
      height: 68px;
      /* 固定高度 */

      /deep/ .el-card__body {
        padding: 8px;
        height: 100%;
      }

      .card-content-wrapper {
        display: flex;
        align-items: center;
        height: 100%;
        min-height: 68px;
        /* 确保最小高度 */

        .card-icon {
          width: 40px;
          height: 40px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 8px;
          flex-shrink: 0;

          i {
            font-size: 18px;
            color: white;
          }
        }

        .card-info {
          flex: 1;
          min-width: 0;
          /* 防止内容溢出 */

          .card-value {
            font-size: 14px;
            font-weight: 600;
            color: #303133;
            margin-bottom: 2px;
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .card-label {
            font-size: 11px;
            color: #606266;
            line-height: 1.2;
            margin-bottom: 2px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .card-trend {
            display: flex;
            align-items: center;
            font-size: 10px;

            i {
              font-size: 9px;
              margin-right: 1px;
            }
          }
        }
      }
    }
  }
}

// 统计进度容器
.progress-container {
  .progress-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    .progress-chart {
      width: 100%;
      max-width: 400px;

      .progress-item {
        margin-bottom: 15px;

        .progress-info {
          display: flex;
          justify-content: space-between;
          margin-bottom: 5px;
          align-items: center;

          .progress-label {
            font-size: 13px;
            color: #606266;
          }

          .progress-value {
            font-size: 13px;
            font-weight: 600;
            color: #409EFF;
          }
        }

        .progress-amount {
          font-size: 11px;
          color: #909399;
          margin-top: 4px;
          text-align: right;
        }

        /deep/ .el-progress-bar {
          padding-right: 0;
        }
      }
    }
  }
}

// 环形百分比容器
.ring-container {
  .ring-content {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    .ring-charts {
      display: flex;
      justify-content: space-around;
      width: 100%;
      max-width: 500px;

      .ring-item {
        display: flex;
        flex-direction: column;
        align-items: center;

        .ring-chart {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: #f0f0f0;
          position: relative;
          margin-bottom: 8px;

          .ring-progress {
            width: 100%;
            height: 100%;
            border-radius: 50%;
            position: relative;

            .ring-inner {
              position: absolute;
              top: 10px;
              left: 10px;
              right: 10px;
              bottom: 10px;
              background: white;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;

              .ring-value {
                font-size: 14px;
                font-weight: 600;
              }
            }
          }
        }

        .ring-info {
          text-align: center;

          .ring-label {
            font-size: 12px;
            color: #606266;
            display: block;
            margin-bottom: 2px;
          }

          .ring-amount {
            font-size: 11px;
            color: #909399;
          }
        }
      }
    }
  }
}

// 图表容器
.chart-container {
  .chart-content {
    flex: 1;
    min-height: 0;

    #chart-container {
      width: 100%;
      height: 100%;
    }
  }
}

// 表格容器样式优化
.table-container {

  .table-content {
    flex: 1;
    min-height: 0;
    overflow: hidden;

    /deep/ .el-table {
      height: 100%;

      .el-table__header-wrapper,
      .el-table__body-wrapper {
        // overflow-x: auto;
      }

      .el-table__fixed,
      .el-table__fixed-right {
        height: 100% !important;
      }

      .el-table__cell {
        white-space: nowrap;
        min-width: 120px;
        color: #606266;
        /* 黑灰色 */

        &.is-fixed-left,
        &.is-fixed-right {
          z-index: 2;
        }
      }

      .el-table__body-wrapper {
        &::-webkit-scrollbar {
          height: 8px;
        }

        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;

          &:hover {
            background: #a8a8a8;
          }
        }
      }
    }
  }
}

// 智能布局提示
.layout-suggestions {
  background: linear-gradient(135deg, #fdf6ec 0%, #f5f7fa 100%);
  border: 1px solid #f0c38e;
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
  position: relative;
  z-index: 1;

  .suggestion-header {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    i {
      color: #E6A23C;
      margin-right: 8px;
      font-size: 16px;
    }

    span {
      font-weight: 600;
      color: #E6A23C;
      font-size: 14px;
    }
  }

  .suggestion-content {
    p {
      color: #E6A23C;
      margin: 0;
      font-size: 13px;
      line-height: 1.4;
    }
  }
}

// 空状态样式
.empty-workbench {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  text-align: center;

  .empty-content {
    color: #909399;

    i {
      font-size: 64px;
      margin-bottom: 16px;
      opacity: 0.5;
      color: #C0C4CC;
    }

    h3 {
      margin: 0 0 8px 0;
      font-size: 18px;
      color: #606266;
      font-weight: 600;
    }

    p {
      margin: 0 0 20px 0;
      font-size: 14px;
      color: #909399;
    }

    .setup-layout-btn {
      padding: 10px 20px;
      font-size: 14px;

      i {
        font-size: 14px;
        margin-right: 5px;
        opacity: 1;
        color: inherit;
      }
    }
  }
}

// 响应式设计
/* 响应式调整 */
@media (max-width: 1200px) {
  .row.first-row {
    .calendar-col {
      flex: 1;
      /* 中等屏幕调整比例 */
    }

    .cards-col {
      flex: 2;
    }
  }
}

@media (max-width: 768px) {
  .row.first-row {
    flex-direction: column;

    .calendar-col,
    .cards-col {
      flex: none;
      height: 300px;
    }
  }

  .calendar-container {
    .calendar-content {
      .calendar-wrapper {
        min-height: 300px;

        .calendar-days {
          .calendar-day {
            font-size: 10px;

            .day-number {
              font-size: 10px;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 992px) {
  .layout-container {
    gap: 16px;
  }

  .row {
    gap: 16px;

    &.first-row {
      .calendar-col {
        height: 380px;
      }
    }

    &.second-row {
      flex-direction: column;
      height: auto;

      .progress-col,
      .ring-col {
        height: 200px;
      }
    }

    &.third-row {
      height: 350px;
    }

    &.fourth-row {
      height: 450px;
    }
  }

  .ring-charts {
    flex-wrap: wrap;
    justify-content: center;
    gap: 20px;

    .ring-item {
      flex: 0 0 calc(50% - 20px);
      margin-bottom: 20px;
    }
  }
}

@media (max-width: 768px) {
  .boss-dashboard {
    padding: 12px;
  }

  .workbench-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;

    .el-tag {
      align-self: flex-start;
    }
  }

  .row {
    flex-direction: column;

    &.first-row {

      .calendar-col,
      .cards-col {
        flex: none;
        height: auto;
      }
    }

    &.second-row {

      .progress-col,
      .ring-col {
        height: 180px;
      }
    }

    &.third-row {
      height: 300px;
    }

    &.fourth-row {
      height: 400px;
    }
  }

  .cards-grid {
    grid-template-columns: 1fr !important;
    grid-template-rows: repeat(8, 1fr);
    gap: 8px;
  }

  .calendar-container {
    .calendar-content {
      .calendar-wrapper {
        .calendar-days {
          .calendar-day {
            font-size: 11px;

            .day-number {
              font-size: 11px;
            }
          }
        }
      }
    }
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;

    .date-range-selector {
      width: 100%;

      /deep/ .el-range-editor {
        width: 100% !important;
      }
    }
  }
}

@media (max-width: 480px) {
  .boss-dashboard {
    padding: 8px;
  }

  .row {
    gap: 12px;

    &.first-row {
      .calendar-col {
        height: 320px;
      }
    }

    &.third-row {
      height: 250px;
    }

    &.fourth-row {
      height: 350px;
    }
  }

  .calendar-container,
  .progress-container,
  .ring-container,
  .chart-container,
  .table-container,
  .cards-container {
    padding: 12px;
  }

  .stat-card {
    /deep/ .el-card__body {
      padding: 12px !important;
    }

    .card-content-wrapper {
      .card-icon {
        width: 40px !important;
        height: 40px !important;

        i {
          font-size: 20px !important;
        }
      }

      .card-info {
        .card-value {
          font-size: 16px !important;
        }
      }
    }
  }

  .ring-charts {
    .ring-item {
      flex: 0 0 100%;
      margin-bottom: 15px;
    }
  }
}

// 动画效果
.calendar-container,
.progress-container,
.ring-container,
.chart-container,
.table-container,
.cards-container {
  animation: fadeInUp 0.5s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// 统计卡片悬停效果
.stat-card {
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }
}

// 空状态动画
.empty-workbench {
  animation: fadeIn 0.6s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

// 表格滚动条样式优化
/deep/ .el-table__body-wrapper {
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;

    &:hover {
      background: #a8a8a8;
    }
  }
}

// 日历日期选择器样式优化
/deep/ .el-date-editor {
  &.el-range-editor {
    .el-range-input {
      font-size: 12px;
    }

    .el-range-separator {
      font-size: 12px;
    }
  }
}

// 进度条样式优化
/deep/ .el-progress {
  .el-progress-bar {
    .el-progress-bar__outer {
      border-radius: 4px;
    }

    .el-progress-bar__inner {
      border-radius: 4px;
      transition: all 0.3s ease;
    }
  }
}

// 确保所有元素正确计算尺寸
.boss-dashboard * {
  box-sizing: border-box;
}

// 防止内容溢出
.boss-dashboard {
  -webkit-overflow-scrolling: touch;
}

// 加载动画优化
.loading-container {
  .loading-content {
    background: white;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

    i {
      animation: rotate 1.5s linear infinite;
    }

    span {
      font-size: 14px;
      margin-top: 10px;
    }
  }
}

@keyframes rotate {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

// 卡片网格响应式优化
@media (max-width: 1400px) {
  .cards-grid {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
  }
}

@media (max-width: 992px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(4, 1fr);
  }
}

@media (max-width: 576px) {
  .cards-grid {
    grid-template-columns: 1fr;
    grid-template-rows: repeat(8, 1fr);
  }
}

// 环形图表响应式优化
@media (max-width: 768px) {
  .ring-charts {
    flex-direction: column;
    align-items: center;

    .ring-item {
      margin-bottom: 20px;
      width: 100%;
      max-width: 200px;
    }
  }
}

// 图表容器响应式优化
.chart-content {
  position: relative;

  #chart-container {
    min-height: 300px;

    @media (max-width: 768px) {
      min-height: 250px;
    }

    @media (max-width: 480px) {
      min-height: 200px;
    }
  }
}

// 表格容器响应式优化
.table-content {

  /deep/ .el-table {
    .el-table__header-wrapper,
    .el-table__body-wrapper {
      // overflow-x: auto;
    }

    .el-table__cell {
      white-space: nowrap;
      min-width: 120px;
    }
  }
}

// 打印样式优化
@media print {
  .boss-dashboard {
    background: white;
    padding: 0;

    .workbench-header {
      page-break-after: avoid;
    }

    .row {
      page-break-inside: avoid;
    }

    .calendar-container,
    .progress-container,
    .ring-container,
    .chart-container,
    .table-container,
    .cards-container {
      box-shadow: none;
      border: 1px solid #ddd;
    }
  }
}

// 高对比度模式支持
@media (prefers-contrast: high) {
  .boss-dashboard {

    .calendar-container,
    .progress-container,
    .ring-container,
    .chart-container,
    .table-container,
    .cards-container {
      border: 2px solid #000;
    }

    .stat-card {
      border: 1px solid #000;
    }
  }
}

// 减少动画支持
@media (prefers-reduced-motion: reduce) {

  .calendar-container,
  .progress-container,
  .ring-container,
  .chart-container,
  .table-container,
  .cards-container {
    animation: none;
    transition: none;
  }

  .stat-card::before {
    display: none;
  }
}
</style>